'use client';

/**
 * Hook for accessing cached token metadata
 *
 * Fetches token metadata from the API cache (Redis-backed) and provides
 * a lookup function. Falls back to on-chain RPC calls for any tokens
 * not found in the cache.
 */
import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { Address } from 'viem';
import { readContracts } from '@wagmi/core';
import { config } from '@/config/index';
import type { ChainType } from '@/constants/chain-specific';

// Compact format: [name, symbol, decimals]
type TokenMetadataTuple = [string, string, number];

// Cache response structure
interface TokenMetadataCacheResponse {
  chainId: number;
  tokens: Record<string, TokenMetadataTuple>;
  totalTokens: number;
  lastUpdated?: number;
  message?: string;
}

// Token info for consumer
export interface CachedTokenInfo {
  address: Address;
  name: string;
  symbol: string;
  decimals: number;
}

// ERC20 ABI for fallback RPC calls
const ERC20_METADATA_ABI = [{
  inputs: [],
  name: 'name',
  outputs: [{
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'symbol',
  outputs: [{
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'decimals',
  outputs: [{
    type: 'uint8'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;

/**
 * Fetch token metadata from cache API
 */
async function fetchTokenMetadataCache(chainId: ChainType): Promise<TokenMetadataCacheResponse> {
  const response = await fetch(`/api/pools/tokens?chainId=${chainId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch token metadata: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch token metadata from chain (RPC fallback)
 */
async function fetchTokenMetadataFromChain(tokenAddresses: Address[], chainId: ChainType): Promise<Map<Address, CachedTokenInfo>> {
  if (tokenAddresses.length === 0) return new Map();
  const results = new Map<Address, CachedTokenInfo>();
  try {
    // Create contract calls for name, symbol, decimals
    const contracts = tokenAddresses.flatMap(addr => [{
      address: addr,
      abi: ERC20_METADATA_ABI,
      functionName: 'name' as const,
      chainId
    }, {
      address: addr,
      abi: ERC20_METADATA_ABI,
      functionName: 'symbol' as const,
      chainId
    }, {
      address: addr,
      abi: ERC20_METADATA_ABI,
      functionName: 'decimals' as const,
      chainId
    }]);
    const callResults = await readContracts(config, {
      contracts
    });

    // Parse results (3 calls per token: name, symbol, decimals)
    for (let i = 0; i < tokenAddresses.length; i++) {
      const nameResult = callResults[i * 3];
      const symbolResult = callResults[i * 3 + 1];
      const decimalsResult = callResults[i * 3 + 2];
      results.set(tokenAddresses[i], {
        address: tokenAddresses[i],
        name: nameResult.status === 'success' ? nameResult.result as string : 'Unknown',
        symbol: symbolResult.status === 'success' ? symbolResult.result as string : 'UNK',
        decimals: decimalsResult.status === 'success' ? decimalsResult.result as number : 18
      });
    }
    return results;
  } catch (error) {
    console.error(`[useCachedTokenMetadata] Error fetching from chain:`, error);
    return results;
  }
}

/**
 * Hook to access cached token metadata with automatic RPC fallback
 */
export function useCachedTokenMetadata(chainId) {
  const $ = _c(17);
  let t0;
  if ($[0] !== chainId) {
    t0 = {
      queryKey: ["tokenMetadataCache", chainId],
      queryFn: () => fetchTokenMetadataCache(chainId),
      staleTime: 300000,
      gcTime: 1800000,
      retry: 1
    };
    $[0] = chainId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data: cacheData,
    isLoading,
    error
  } = useQuery(t0);
  let t1;
  if ($[2] !== cacheData) {
    t1 = tokenAddress => {
      if (!cacheData?.tokens) {
        return null;
      }
      const metadata = cacheData.tokens[tokenAddress.toLowerCase()];
      if (!metadata) {
        return null;
      }
      const [name, symbol, decimals] = metadata;
      return {
        address: tokenAddress,
        name,
        symbol,
        decimals
      };
    };
    $[2] = cacheData;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getFromCache = t1;
  let t2;
  if ($[4] !== chainId || $[5] !== getFromCache) {
    t2 = async tokenAddress_0 => {
      const cached = getFromCache(tokenAddress_0);
      if (cached) {
        return cached;
      }
      console.log(`[useCachedTokenMetadata] Cache miss for ${tokenAddress_0}, fetching from chain`);
      const fromChain = await fetchTokenMetadataFromChain([tokenAddress_0], chainId);
      return fromChain.get(tokenAddress_0) || {
        address: tokenAddress_0,
        name: "Unknown",
        symbol: "UNK",
        decimals: 18
      };
    };
    $[4] = chainId;
    $[5] = getFromCache;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const getTokenInfo = t2;
  let t3;
  if ($[7] !== chainId || $[8] !== getFromCache) {
    t3 = async tokenAddresses => {
      const results = new Map();
      const needsRpc = [];
      for (const address of tokenAddresses) {
        const cached_0 = getFromCache(address);
        if (cached_0) {
          results.set(address, cached_0);
        } else {
          needsRpc.push(address);
        }
      }
      if (tokenAddresses.length > 0) {
        const cacheHits = tokenAddresses.length - needsRpc.length;
        console.log(`[useCachedTokenMetadata] Cache: ${cacheHits}/${tokenAddresses.length} hits ` + `(${needsRpc.length} RPC calls needed)`);
      }
      if (needsRpc.length > 0) {
        const fromChain_0 = await fetchTokenMetadataFromChain(needsRpc, chainId);
        for (const [address_0, info] of fromChain_0) {
          results.set(address_0, info);
        }
      }
      return results;
    };
    $[7] = chainId;
    $[8] = getFromCache;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const getMultipleTokenInfo = t3;
  const t4 = cacheData?.totalTokens || 0;
  let t5;
  if ($[10] !== error || $[11] !== getFromCache || $[12] !== getMultipleTokenInfo || $[13] !== getTokenInfo || $[14] !== isLoading || $[15] !== t4) {
    t5 = {
      isLoading,
      error,
      totalCachedTokens: t4,
      getFromCache,
      getTokenInfo,
      getMultipleTokenInfo
    };
    $[10] = error;
    $[11] = getFromCache;
    $[12] = getMultipleTokenInfo;
    $[13] = getTokenInfo;
    $[14] = isLoading;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}

/**
 * Standalone function to get token metadata with cache fallback
 * Useful when hook cannot be used (e.g., in non-React context)
 */
export async function getTokenMetadataWithFallback(tokenAddresses: Address[], chainId: ChainType): Promise<Map<Address, CachedTokenInfo>> {
  const results = new Map<Address, CachedTokenInfo>();
  const needsRpc: Address[] = [];
  try {
    // Try to fetch cache
    const cacheResponse = await fetchTokenMetadataCache(chainId);
    if (cacheResponse?.tokens) {
      // Check cache for each token
      for (const address of tokenAddresses) {
        const metadata = cacheResponse.tokens[address.toLowerCase()];
        if (metadata) {
          const [name, symbol, decimals] = metadata;
          results.set(address, {
            address,
            name,
            symbol,
            decimals
          });
        } else {
          needsRpc.push(address);
        }
      }
      console.log(`[getTokenMetadataWithFallback] Cache: ${results.size}/${tokenAddresses.length} hits`);
    } else {
      // No cache available, all need RPC
      needsRpc.push(...tokenAddresses);
    }
  } catch (error) {
    console.warn('[getTokenMetadataWithFallback] Cache fetch failed, using RPC:', error);
    needsRpc.push(...tokenAddresses);
  }

  // Fetch missing from chain
  if (needsRpc.length > 0) {
    console.log(`[getTokenMetadataWithFallback] Fetching ${needsRpc.length} tokens from chain`);
    const fromChain = await fetchTokenMetadataFromChain(needsRpc, chainId);
    for (const [address, info] of fromChain) {
      results.set(address, info);
    }
  }
  return results;
}